import { INVOICE_TRANSITIONS } from '@rfleet/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Banknote, ExternalLink, FileDown, Pencil, Printer, Send, Trash2 } from 'lucide-react';
import { useCallback, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Badge, Dialog, Field, PageHeader, Skeleton, Spinner, useConfirm, useToast } from '../../components/ui';
import { api } from '../../lib/api';
import { fmtDate, fmtDateTime, fmtEgp, fmtNumber } from '../../lib/format';
import { useSession } from '../../lib/session';
import { useSocketEvent } from '../../lib/socket';
import { invoiceTone, type InvoiceDetail } from './types';

export function InvoiceDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDialog = useConfirm();
  const can = useSession((s) => s.can);
  const [paying, setPaying] = useState(false);
  const [amount, setAmount] = useState('');
  const q = useQuery({ queryKey: ['invoice', id], queryFn: () => api<InvoiceDetail>(`/invoices/${id}`) });
  useSocketEvent('invoice.updated', useCallback((e: { invoiceId: string }) => { if (e.invoiceId === id) void qc.invalidateQueries({ queryKey: ['invoice', id] }); }, [id, qc]));

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['invoice', id] });
    void qc.invalidateQueries({ queryKey: ['invoices'] });
  };
  const act = useMutation({
    mutationFn: ({ action, body }: { action: 'send' | 'mark-paid' | 'cancel'; body?: unknown }) => api(`/invoices/${id}/${action}`, { method: 'POST', json: body ?? {} }),
    onSuccess: () => { toast.success(t('common.saved')); setPaying(false); setAmount(''); refresh(); },
    onError: toast.error,
  });
  const pdf = useMutation({
    mutationFn: () => api<{ url: string }>(`/invoices/${id}/pdf?lang=${i18n.language === 'en' ? 'en' : 'ar'}`, { method: 'POST' }),
    onSuccess: (r) => { window.open(r.url, '_blank', 'noopener'); refresh(); },
    onError: toast.error,
  });
  const remove = useMutation({ mutationFn: () => api(`/invoices/${id}`, { method: 'DELETE' }), onSuccess: () => { toast.success(t('common.saved')); void qc.invalidateQueries({ queryKey: ['invoices'] }); nav('/invoices'); }, onError: toast.error });

  if (q.isLoading || !q.data) return <Skeleton className="h-96" />;
  const inv = q.data;
  const lang = i18n.language;
  const allowed = INVOICE_TRANSITIONS[inv.status];
  const canEdit = can('invoices:update');
  const late = inv.status === 'OVERDUE';

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={<span className="flex items-center gap-2"><span dir="ltr">{inv.number}</span><Badge tone={invoiceTone(inv.status)} dot>{t(`invoices.statuses.${inv.status}`)}</Badge></span>}
        subtitle={`${inv.customer.name} · ${fmtDate(inv.issueDate, lang)}`}
        actions={
          <div className="flex flex-wrap gap-2">
            <Link to={`/invoices/${inv.id}/print`} className="btn-secondary"><Printer size={16} /> {t('invoices.print')}</Link>
            {allowed.includes('pdf') ? (
              inv.pdfUrl ? <a href={inv.pdfUrl} target="_blank" rel="noreferrer" className="btn-secondary"><FileDown size={16} /> PDF</a> : inv.pdfAvailable ? <button className="btn-secondary" disabled={pdf.isPending} onClick={() => pdf.mutate()}>{pdf.isPending ? <Spinner /> : <FileDown size={16} />} {t('invoices.generatePdf')}</button> : null
            ) : null}
            {canEdit && allowed.includes('update') ? <Link to={`/invoices/${inv.id}/edit`} className="btn-secondary"><Pencil size={16} /> {t('common.edit')}</Link> : null}
            {canEdit && allowed.includes('send') ? <button className="btn-primary" disabled={act.isPending} onClick={() => act.mutate({ action: 'send' })}><Send size={16} /> {t('invoices.actions.send')}</button> : null}
            {canEdit && allowed.includes('pay') ? <button className="btn-primary" onClick={() => { setAmount(String(inv.balanceEgp)); setPaying(true); }}><Banknote size={16} /> {t('invoices.actions.pay')}</button> : null}
            {canEdit && allowed.includes('cancel') ? <button className="btn-ghost text-danger-fg" disabled={act.isPending} onClick={() => confirmDialog(t('invoices.cancelConfirm'), { danger: true }).then((ok) => ok && act.mutate({ action: 'cancel' }))}><Ban size={16} /> {t('invoices.actions.cancel')}</button> : null}
            {can('invoices:delete') && allowed.includes('delete') ? <button className="btn-ghost text-danger-fg" onClick={() => confirmDialog(t('invoices.deleteConfirm'), { danger: true }).then((ok) => ok && remove.mutate())}><Trash2 size={16} /> {t('common.delete')}</button> : null}
          </div>
        }
      />
      {late ? <div className="rounded-xl border border-danger/30 bg-danger-bg px-4 py-3 text-sm text-danger-fg">{t('invoices.overdueBanner', { date: fmtDate(inv.dueDate, lang) })}</div> : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="flex flex-col gap-4 lg:col-span-2">
          <section className="card">
            <table className="w-full text-sm">
              <thead className="text-xs text-text-muted">
                <tr><th className="pb-2 text-start">#</th><th className="pb-2 text-start">{t('invoices.itemDescription')}</th><th className="pb-2 text-end">{t('invoices.qty')}</th><th className="pb-2 text-end">{t('invoices.unitPrice')}</th><th className="pb-2 text-end">{t('invoices.lineTotal')}</th></tr>
              </thead>
              <tbody>
                {inv.items.map((i, idx) => (
                  <tr key={i.id} className="border-t border-border-soft">
                    <td className="py-2 text-text-faint">{idx + 1}</td>
                    <td className="py-2">
                      <div className="font-semibold">{i.description}</div>
                      <div className="text-xs text-text-faint">{t(`invoices.kinds.${i.kind}`)}{i.expenseId ? <> · <Link to={`/expenses/${i.expenseId}`} className="hover:underline">{t('invoices.viewExpense')}</Link></> : null}</div>
                    </td>
                    <td className="py-2 text-end tabular-nums">{fmtNumber(i.quantity, lang, 2)}</td>
                    <td className="py-2 text-end tabular-nums">{fmtEgp(i.unitPriceEgp, lang)}</td>
                    <td className="py-2 text-end font-semibold tabular-nums">{fmtEgp(i.totalEgp, lang)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <dl className="ms-auto mt-4 flex max-w-xs flex-col gap-1 border-t border-border pt-3 text-sm">
              <div className="flex justify-between"><dt className="text-text-muted">{t('invoices.subtotal')}</dt><dd className="tabular-nums">{fmtEgp(inv.subtotalEgp, lang)}</dd></div>
              <div className="flex justify-between"><dt className="text-text-muted">{t('invoices.vat')} ({fmtNumber(inv.taxRatePct, lang, 2)}%)</dt><dd className="tabular-nums">{fmtEgp(inv.taxEgp, lang)}</dd></div>
              <div className="flex justify-between text-lg font-bold"><dt>{t('invoices.total')}</dt><dd className="tabular-nums">{fmtEgp(inv.totalEgp, lang)}</dd></div>
              {Number(inv.paidEgp) > 0 ? (
                <>
                  <div className="flex justify-between text-success-fg"><dt>{t('invoices.paid')}</dt><dd className="tabular-nums">{fmtEgp(inv.paidEgp, lang)}</dd></div>
                  <div className="flex justify-between font-semibold"><dt>{t('invoices.balance')}</dt><dd className="tabular-nums">{fmtEgp(inv.balanceEgp, lang)}</dd></div>
                </>
              ) : null}
            </dl>
            {inv.notes ? <p className="mt-3 whitespace-pre-wrap border-t border-border-soft pt-3 text-sm text-text-2">{inv.notes}</p> : null}
          </section>
        </div>

        <div className="flex flex-col gap-4">
          <section className="card">
            <h2 className="mb-3 text-sm font-bold text-text-2">{t('invoices.customer')}</h2>
            <div className="font-bold">{inv.customer.name}</div>
            <dl className="mt-2 grid gap-y-1 text-sm">
              {inv.customer.contactName ? <Row label={t('customers.contact')}>{inv.customer.contactName}</Row> : null}
              {inv.customer.phone ? <Row label={t('customers.phone')}><span dir="ltr">{inv.customer.phone}</span></Row> : null}
              {inv.customer.taxId ? <Row label={t('customers.taxId')}><span dir="ltr">{inv.customer.taxId}</span></Row> : null}
              {inv.customer.address ? <Row label={t('customers.address')}>{inv.customer.address}</Row> : null}
            </dl>
          </section>
          <section className="card">
            <h2 className="mb-3 text-sm font-bold text-text-2">{t('invoices.details')}</h2>
            <dl className="grid gap-y-1 text-sm">
              <Row label={t('invoices.issueDate')}>{fmtDate(inv.issueDate, lang)}</Row>
              <Row label={t('invoices.dueDate')}>{fmtDate(inv.dueDate, lang)}</Row>
              {inv.sentAt ? <Row label={t('invoices.sentAt')}>{fmtDateTime(inv.sentAt, lang)}</Row> : null}
              {inv.paidAt ? <Row label={t('invoices.paidAt')}>{fmtDateTime(inv.paidAt, lang)}</Row> : null}
              <Row label={t('invoices.trip')}>{inv.trip ? <Link to={`/trips/${inv.trip.id}`} className="hover:underline" dir="ltr">{inv.trip.number}</Link> : '—'}</Row>
              {inv.trip?.distanceKm ? <Row label={t('trips.distance')}>{fmtNumber(inv.trip.distanceKm, lang, 1)} {t('units.km')}</Row> : null}
              <Row label={t('invoices.createdBy')}>{inv.createdByName ?? '—'}</Row>
            </dl>
            {inv.uploadedUrl ? <a href={inv.uploadedUrl} target="_blank" rel="noreferrer" className="btn-secondary mt-3 w-full"><ExternalLink size={16} /> {t('invoices.openUploaded')}</a> : inv.uploadedFileId ? <p className="mt-3 text-xs text-text-muted">{t('invoices.uploadedUnavailable')}</p> : null}
          </section>
        </div>
      </div>

      <Dialog
        open={paying}
        onClose={() => setPaying(false)}
        title={t('invoices.actions.pay')}
        footer={
          <>
            <button className="btn-secondary" onClick={() => setPaying(false)}>{t('common.cancel')}</button>
            <button className="btn-primary" disabled={!(Number(amount) > 0) || Number(amount) > inv.balanceEgp + 0.005 || act.isPending} onClick={() => act.mutate({ action: 'mark-paid', body: { amountEgp: Number(amount) } })}>{act.isPending ? <Spinner /> : <Banknote size={16} />} {t('invoices.confirmPayment')}</button>
          </>
        }
      >
        <p className="mb-3 text-sm text-text-muted">{t('invoices.balance')}: <strong className="text-text">{fmtEgp(inv.balanceEgp, lang)}</strong></p>
        <Field label={t('invoices.paymentAmount')} required hint={t('invoices.partialHint')}>{(fid) => <input id={fid} type="number" min={0.01} max={inv.balanceEgp} step={0.01} dir="ltr" className="input text-lg font-bold" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />}</Field>
      </Dialog>
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-text-muted">{label}</dt>
      <dd className="text-end font-semibold">{children}</dd>
    </div>
  );
}
